// NIFC aviation-frequency repeaters layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.
//
// Uses the LayerFamilySpec shape introduced in batch 9.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { ensureRepeaterIcons, REPEATER_ICON_EXPRESSION } from '../repeaterIcons';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

/**
 * Mount the repeaters source + its layers. Idempotent on the source.
 */
export function addRepeatersLayers(map: MlMap): void {
    if (!map.getSource('repeaters')) {
      map.addSource('repeaters', { type: 'geojson', data: EMPTY_FC });
      // Kick off SVG icon registration; the layer paints once images
      // are registered (MapLibre redraws on image-load).
      void ensureRepeaterIcons(map);
      map.addLayer({
        id: 'repeaters-icon',
        type: 'symbol',
        source: 'repeaters',
        minzoom: 6,
        layout: {
          visibility: 'none',
          'icon-image': REPEATER_ICON_EXPRESSION,
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            6, 0.55,
            9, 0.75,
            12, 1.0,
          ],
          'icon-anchor': 'bottom',  // base of the mast = lat/lon
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: 'repeaters-label',
        type: 'symbol',
        source: 'repeaters',
        minzoom: 9,
        layout: {
          visibility: 'none',
          'text-field': ['coalesce', ['get', 'site'], ''],
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            9, 10, 11, 11, 13, 12,
          ],
          'text-anchor': 'top',
          'text-offset': [0, 1.0],
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'text-padding': 2,
        },
        paint: {
          'text-color': '#a7f3d0',         // emerald-200
          'text-halo-color': '#0b0d0f',
          'text-halo-width': 1.4,
        },
      });
    }
}

registerLayerFamily({
  id: 'repeaters',
  label: 'NIFC aviation-frequency repeaters',
  sourceId: 'repeaters',
  layerIds: ['repeaters-icon', 'repeaters-label'],
  mount: (map) => addRepeatersLayers(map),
  visibilitySelector: (l) => l.repeaters,
  applyState: (map) => {
    if (!map.getLayer('repeaters-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.repeaters;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('repeaters-icon', 'visibility', v);
    if (map.getLayer('repeaters-label')) {
      map.setLayoutProperty('repeaters-label', 'visibility', v);
    }
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('repeaters');
    if (!spec) return;
    markOverlayLoaded('repeaters', true);
    return refreshOverlay(map, spec);
  },
});
